import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { faChevronLeft, faPenToSquare } from '@fortawesome/pro-regular-svg-icons';
import { Avatar } from '@/modules/Avatar';
import type { AvatarProps } from '@/modules/Avatar/types';
import { cn } from '@/shared/utils/cn';
import { Button } from '@/modules/Button';
import type {
  SidebarDrilldownPanelProps,
  SidebarProps,
  SidebarUserProfileProps,
  SidebarWorkspaceCardProps,
  SidebarWorkspaceConfig,
} from '../../types';
import { SidebarDivider } from '../atoms';
import {
  SidebarDrilldownPanel,
  SidebarMenuGroup,
  SidebarUserProfile,
  SidebarWorkspaceCard,
} from '../molecules';

function toWorkspaceCardProps(
  config?: SidebarWorkspaceConfig
): SidebarWorkspaceCardProps | undefined {
  if (!config) return undefined;
  const {
    workspaceSheet: _ws,
    workspaceSheetOpen: _o,
    onWorkspaceSheetOpenChange: _oc,
    ...card
  } = config;
  return card;
}

function renderCollapsedUserAvatar(profile: SidebarUserProfileProps) {
  const avatarType = profile.summary.avatarProps?.type ?? 'people';
  const { type: _t, ...restAvatarProps } = profile.summary.avatarProps ?? {};
  return <Avatar {...({ type: avatarType, size: 'sm', ...restAvatarProps } as AvatarProps)} />;
}

/**
 * Application sidebar aligned with the Sidebar-Menu Figma spec: 240px expanded, 56px collapsed,
 * 12px rail padding, header row (logo + optional trailing), collapse control with shadow-03,
 * scrollable menu region, workspace card below the menu (or sheet over profile when `workspace.workspaceSheet`),
 * then profile and optional footer.
 *
 * @example
 * ```tsx
 * <Sidebar
 *   logo="/logo.png"
 *   headerTrailing={<NotificationsButton />}
 *   workspace={{ title: "Workspace", projectId: "PRJ-001" }}
 *   menuGroups={[ { title: "SECTION", items: [...] } ]}
 *   userProfile={{
 *     summary: { name: "Jane", subtitle: "Admin", avatarProps: { type: "people", src: "/a.jpg" } },
 *     menu: { onLogout: () => {} },
 *   }}
 *   footer={<span>Legales · v1.0</span>}
 * />
 * ```
 */
export const Sidebar: React.FC<SidebarProps> = ({
  logo,
  onLogoClick,
  headerTrailing,
  workspace,
  menuGroups = [],
  userProfile,
  footer,
  collapsed = false,
  onToggleCollapse,
  className,
  width = 240,
  collapsedWidth = 56,
  drilldown,
}) => {
  const sidebarWidth = collapsed ? collapsedWidth : width;

  const workspaceCardProps = useMemo(() => toWorkspaceCardProps(workspace), [workspace]);
  const workspaceSheetEnabled = Boolean(workspace?.workspaceSheet);
  const controlledWorkspaceSheet =
    workspace?.workspaceSheetOpen !== undefined &&
    workspace?.onWorkspaceSheetOpenChange !== undefined;
  const [workspaceSheetInternalOpen, setWorkspaceSheetInternalOpen] = useState(false);

  const workspaceSheetOpen = controlledWorkspaceSheet
    ? Boolean(workspace?.workspaceSheetOpen)
    : workspaceSheetInternalOpen;

  const setWorkspaceSheetOpen = useCallback(
    (next: boolean) => {
      if (controlledWorkspaceSheet) {
        workspace?.onWorkspaceSheetOpenChange?.(next);
      } else {
        setWorkspaceSheetInternalOpen(next);
      }
    },
    [controlledWorkspaceSheet, workspace]
  );

  /**
   * While collapsing, keep `SidebarWorkspaceCard` mounted until `max-height` ends (avoids Card→Button swap “double” motion).
   */
  const [workspaceSheetCloseHoldCard, setWorkspaceSheetCloseHoldCard] = useState(false);
  const workspaceSheetPrevOpenRef = useRef(workspaceSheetOpen);

  /**
   * After open=true, wait two rAF frames before applying expanded `max-height` so the browser can transition from the collapsed strip.
   */
  const [workspaceSheetOpenExpandCommitted, setWorkspaceSheetOpenExpandCommitted] = useState(true);

  useLayoutEffect(() => {
    if (!workspaceSheetEnabled) {
      setWorkspaceSheetCloseHoldCard(false);
      workspaceSheetPrevOpenRef.current = false;
      setWorkspaceSheetOpenExpandCommitted(true);
      return;
    }
    if (workspaceSheetOpen) {
      setWorkspaceSheetCloseHoldCard(false);
      workspaceSheetPrevOpenRef.current = true;
      return;
    }
    const reducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) {
      setWorkspaceSheetCloseHoldCard(false);
      workspaceSheetPrevOpenRef.current = false;
      return;
    }
    if (workspaceSheetPrevOpenRef.current) {
      setWorkspaceSheetCloseHoldCard(true);
    }
    workspaceSheetPrevOpenRef.current = false;
  }, [workspaceSheetOpen, workspaceSheetEnabled]);

  useLayoutEffect(() => {
    if (!workspaceSheetEnabled || !workspaceSheetOpen) {
      setWorkspaceSheetOpenExpandCommitted(true);
      return;
    }
    const reducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) {
      setWorkspaceSheetOpenExpandCommitted(true);
      return;
    }
    setWorkspaceSheetOpenExpandCommitted(false);
    let raf1 = 0;
    let raf2 = 0;
    raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => {
        setWorkspaceSheetOpenExpandCommitted(true);
      });
    });
    return () => {
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
    };
  }, [workspaceSheetOpen, workspaceSheetEnabled]);

  /** Lag ref so the first paint after `open` flips true is still treated as collapsed (avoids skipping open transition). */
  const workspaceSheetSizingLagRef = useRef(false);
  const workspaceSheetIsOpeningSizingFrame =
    Boolean(workspaceSheetOpen) && !workspaceSheetSizingLagRef.current;

  useLayoutEffect(() => {
    workspaceSheetSizingLagRef.current = workspaceSheetOpen;
  }, [workspaceSheetOpen]);

  const workspaceSheetShowsCard = workspaceSheetOpen || workspaceSheetCloseHoldCard;
  const workspaceSheetPanelExpandedSize =
    Boolean(workspaceSheetOpen) &&
    workspaceSheetOpenExpandCommitted &&
    !workspaceSheetIsOpeningSizingFrame;

  /**
   * Card stays mounted while `max-height` collapses (smooth swap to trigger).
   * Hide it visually — clipping still exposes dashed border / divider edges; `justify-start` helps when reduced motion keeps opacity on.
   */
  const workspaceSheetPanelClosingHold = !workspaceSheetOpen && workspaceSheetCloseHoldCard;

  useEffect(() => {
    if (workspaceSheetOpen || !workspaceSheetCloseHoldCard) return;
    const fallbackMs = 340;
    const id = window.setTimeout(() => setWorkspaceSheetCloseHoldCard(false), fallbackMs);
    return () => window.clearTimeout(id);
  }, [workspaceSheetOpen, workspaceSheetCloseHoldCard]);

  /** After Card→trigger swap on close, avoid InnerButton `transition-all` reading as X-motion; run Y enter-from-top (CSS). */
  const workspaceSheetPrevCloseHoldRef = useRef(workspaceSheetCloseHoldCard);
  const [workspaceSheetTriggerEnterFromTop, setWorkspaceSheetTriggerEnterFromTop] = useState(false);

  useLayoutEffect(() => {
    if (!workspaceSheetEnabled) {
      workspaceSheetPrevCloseHoldRef.current = false;
      return;
    }
    const prevHold = workspaceSheetPrevCloseHoldRef.current;
    workspaceSheetPrevCloseHoldRef.current = workspaceSheetCloseHoldCard;
    if (prevHold && !workspaceSheetCloseHoldCard && !workspaceSheetOpen) {
      const reducedMotion =
        typeof window !== 'undefined' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!reducedMotion) {
        setWorkspaceSheetTriggerEnterFromTop(true);
      }
    }
  }, [workspaceSheetCloseHoldCard, workspaceSheetOpen, workspaceSheetEnabled]);

  useEffect(() => {
    if (!workspaceSheetTriggerEnterFromTop) return;
    const id = window.setTimeout(() => setWorkspaceSheetTriggerEnterFromTop(false), 380);
    return () => window.clearTimeout(id);
  }, [workspaceSheetTriggerEnterFromTop]);

  const workspaceSheetInnerMotionClassName = cn(
    'ui-w-full ui-shrink-0 ui-transform-gpu',
    workspaceSheetPanelClosingHold &&
      'ui-pointer-events-none ui-opacity-0 motion-reduce:ui-pointer-events-auto motion-reduce:ui-opacity-100',
    !workspaceSheetShowsCard && workspaceSheetTriggerEnterFromTop
      ? 'ui-sidebar-workspace-sheet-trigger-enter-from-top'
      : cn(
          !workspaceSheetPanelClosingHold &&
            'ui-transition-transform ui-duration-300 ui-ease-out motion-reduce:ui-transition-none',
          workspaceSheetOpen && workspaceSheetShowsCard && !workspaceSheetPanelExpandedSize
            ? 'ui--translate-y-8'
            : 'ui-translate-y-0'
        )
  );

  const handleWorkspaceSheetPanelTransitionEnd = useCallback(
    (event: React.TransitionEvent<HTMLDivElement>) => {
      if (event.target !== event.currentTarget) return;
      if (event.propertyName !== 'max-height') return;
      if (!workspaceSheetOpen) {
        setWorkspaceSheetCloseHoldCard(false);
      }
    },
    [workspaceSheetOpen]
  );

  let drilldownPanelProps: SidebarDrilldownPanelProps | null = null;
  if (drilldown?.active === true) {
    const { active: _active, ...rest } = drilldown;
    drilldownPanelProps = rest;
  }

  const showDividerBelowHeader = Boolean(logo || headerTrailing);

  /**
   * Collapse control sits half outside the rail (`-right`) for the Figma spec. The scrollable `<aside>`
   * keeps `overflow-x-hidden` so menu content cannot spill horizontally; rendering the toggle on this
   * wrapper (sibling of `<aside>`) avoids clipping the circular control.
   */
  const collapseButtonTopClass = '!ui-top-[40px]';

  return (
    <div
      className="ui-relative ui-h-full ui-min-h-0 ui-shrink-0 ui-overflow-visible"
      style={{ width: sidebarWidth }}
    >
      <aside
        className={cn(
          'ui-imj',
          'ui-relative ui-flex ui-h-full ui-min-h-0 ui-w-full ui-flex-col ui-gap-8',
          // Allow vertical scroll when workspace sheet + profile + footer exceed the rail (avoids clipping the footer).
          'ui-overflow-y-auto ui-overflow-x-hidden',
          'ui-border-r-1 ui-border-default ui-bg-surface-secondary',
          'ui-p-12 ui-transition-all ui-duration-300',
          className
        )}
      >
        {(logo || headerTrailing || onToggleCollapse) && (
          <div
            className={cn(
              'ui-relative ui-mb-0 ui-min-h-32 ui-w-full ui-shrink-0',
              collapsed
                ? 'ui-flex ui-flex-col ui-items-center ui-gap-12'
                : 'ui-flex ui-flex-wrap ui-items-center ui-gap-12'
            )}
          >
            {logo ? (
              <div
                className={cn('ui-flex ui-shrink-0 ui-items-center ui-justify-center', {
                  'ui-cursor-pointer': Boolean(onLogoClick),
                })}
                onClick={onLogoClick}
              >
                {typeof logo === 'string' ? (
                  <img
                    src={logo}
                    alt="Logo"
                    className="ui-size-32 ui-rounded-control-pill ui-object-cover"
                  />
                ) : (
                  logo
                )}
              </div>
            ) : null}

            {!collapsed && headerTrailing ? (
              <div className="ui-flex ui-min-w-0 ui-flex-1 ui-items-start ui-justify-end">
                {headerTrailing}
              </div>
            ) : null}

            {collapsed && headerTrailing ? (
              <div className="ui-flex ui-w-full ui-shrink-0 ui-flex-col ui-items-center">
                {headerTrailing}
              </div>
            ) : null}
          </div>
        )}

        {logo || headerTrailing ? <SidebarDivider className="ui-shrink-0" /> : null}

        <div className="ui-flex ui-min-h-0 ui-w-full ui-flex-1 ui-flex-col ui-gap-8 ui-overflow-hidden">
          <div
            className={cn(
              'custom-scrollbar ui-flex ui-min-h-0 ui-w-full ui-flex-1 ui-flex-col ui-pr-[1px]',
              'ui-overflow-y-auto ui-overflow-x-hidden'
            )}
          >
            {drilldownPanelProps !== null ? (
              <SidebarDrilldownPanel {...drilldownPanelProps} />
            ) : (
              menuGroups.map((group, index) => (
                <SidebarMenuGroup
                  key={group.title || `group-${index}`}
                  {...group}
                  collapsed={collapsed}
                  skipLeadingPlainDivider={
                    index === 0 && showDividerBelowHeader && Boolean(group.title)
                  }
                />
              ))
            )}
          </div>

          {workspaceCardProps && !collapsed && !workspaceSheetEnabled ? (
            <div className="ui-w-full ui-shrink-0">
              <SidebarWorkspaceCard {...workspaceCardProps} />
            </div>
          ) : null}
        </div>

        <div className="ui-mt-auto ui-flex ui-w-full ui-shrink-0 ui-flex-col ui-gap-8">
          {collapsed && userProfile ? (
            <div className="ui-flex ui-w-full ui-justify-center">
              {renderCollapsedUserAvatar(userProfile)}
            </div>
          ) : null}
          {!collapsed && workspaceSheetEnabled && workspaceCardProps ? (
            <div className="ui-flex ui-w-full ui-shrink-0 ui-flex-col ui-gap-8">
              {/*
               * Sheet replaces the trigger row (same slot): closed = compact “Mesa de trabajo” button;
               * open = full SidebarWorkspaceCard. max-height animates from ~trigger height to card height
               * with justify-end so growth reads upward from the button baseline (above the profile).
               */}
              <div
                id="sidebar-workspace-sheet-panel"
                role="region"
                aria-label={workspaceCardProps.sectionLabel ?? 'Mesa de trabajo'}
                onTransitionEnd={handleWorkspaceSheetPanelTransitionEnd}
                className={cn(
                  'ui-flex ui-w-full ui-shrink-0 ui-flex-col ui-overflow-x-hidden',
                  workspaceSheetPanelExpandedSize ? 'ui-overflow-y-auto' : 'ui-overflow-hidden',
                  'ui-transition-[max-height] ui-duration-300 ui-ease-out motion-reduce:ui-transition-none',
                  workspaceSheetPanelExpandedSize
                    ? // Cap sheet height so profile + footer below stay visible; reserve rail chrome vs viewport (`svh` avoids clipping under browser UI).
                      'ui-max-h-[min(28rem,calc(100svh-17.5rem))] ui-min-h-0 ui-justify-end'
                    : workspaceSheetShowsCard
                      ? cn(
                          'ui-max-h-[40px] ui-min-h-[40px]',
                          workspaceSheetPanelClosingHold ? 'ui-justify-start' : 'ui-justify-end'
                        )
                      : 'ui-max-h-[40px] ui-min-h-[40px] ui-justify-center'
                )}
              >
                <div
                  className={workspaceSheetInnerMotionClassName}
                  aria-hidden={workspaceSheetPanelClosingHold || undefined}
                >
                  {workspaceSheetShowsCard ? (
                    <SidebarWorkspaceCard
                      {...workspaceCardProps}
                      onClose={() => {
                        workspaceCardProps.onClose?.();
                        setWorkspaceSheetOpen(false);
                      }}
                    />
                  ) : (
                    <Button
                      type="button"
                      variant="text"
                      color="secondary"
                      theme="solid"
                      size="xs"
                      fullWidth
                      borderable={false}
                      transition={false}
                      aria-expanded={workspaceSheetOpen}
                      aria-controls="sidebar-workspace-sheet-panel"
                      iconProps={{
                        icon: workspaceCardProps.icon ?? faPenToSquare,
                        show: true,
                      }}
                      className="ui-max-h-full ui-min-h-0 ui-shrink-0 ui-justify-start ui-overflow-hidden ui-text-ellipsis"
                      onClick={() => setWorkspaceSheetOpen(true)}
                    >
                      {workspaceCardProps.sectionLabel ?? 'Mesa de trabajo'}
                    </Button>
                  )}
                </div>
              </div>
              {userProfile ? <SidebarUserProfile {...userProfile} /> : null}
            </div>
          ) : userProfile && !collapsed ? (
            <SidebarUserProfile {...userProfile} />
          ) : null}
          {footer ? (
            <div
              className={cn(
                'ui-flex ui-w-full ui-items-center ui-gap-8',
                collapsed ? 'ui-flex-col ui-justify-center ui-text-center' : 'ui-justify-between'
              )}
            >
              {footer}
            </div>
          ) : null}
        </div>
      </aside>

      {onToggleCollapse ? (
        <Button
          type="button"
          size="xs"
          color="primary"
          rounded
          showShadow
          borderable={false}
          onClick={onToggleCollapse}
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className={cn(
            // InnerButton base includes `ui-relative`; without `!important`, `absolute` can lose in the cascade.
            '!ui-absolute ui-z-10',
            // `ui-p-12` top padding + previous in-header offset (28px) so alignment matches the header row.
            collapseButtonTopClass,
            '!-ui-right-[28px]',
            'focus-visible:ui-ring-brand focus-visible:ui-outline-none focus-visible:ui-ring-2 focus-visible:ui-ring-offset-2'
          )}
          iconProps={{
            icon: faChevronLeft,
            color: 'white',
            size: 'md',
            className: cn('ui-transition-transform ui-duration-300', collapsed && 'ui-rotate-180'),
          }}
        />
      ) : null}
    </div>
  );
};

Sidebar.displayName = 'Sidebar';
